import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { Input, Modal } from "antd";
import { CustomInput, CustomSelect, ErrorText } from "./ui";
import { validateFormHandler } from "../utils/validations/validateForm";
import VerifyOtpForm from "./VerifyOtpForm";
import { loginSuccess } from "../slices/authSlice";
import { saveToLocalStorage } from "../utils/localStorage";
import { setIsLoggedIn, setRole } from "../slices/app";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "@greatsumini/react-facebook-login";
import { LinkedIn } from "react-linkedin-login-oauth2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SignUp = ({ toggleForm }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const showPassword = useState(false);
  const showConfirmPassword = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const role = process.env.REACT_APP_ADMIN_ROLE_ID;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [verifyData, setVerifyData] = useState(null);

  const handleSignUp = async (e) => {
    e.preventDefault();
    const validationErrors = validateFormHandler(
      {
        name,
        email,
        baseCurrency,
        signUpPassword: password,
        confirmPassword,
      },
      { name: "person" },
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      if (res.ok) {
        toast.success("OTP sent to your email!");
        setOtpSent(true);
        setVerifyData({
          name,
          email: email.toLowerCase(),
          password,
          baseCurrency,
          role,
          parentId: process.env.REACT_APP_SUPERADMIN_ID,
        });
      } else {
        const err = await res.json();
        toast.error(err.error);
      }
    } catch (error) {
      toast.error("Error sending OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      if (res.ok) {
        toast.success("OTP resent to your email!");
        setOtpSent(true);
      } else {
        const err = await res.json();
        toast.error(err.error);
      }
    } catch (error) {
      toast.error("Error resending OTP");
    }
  };

  const getAllCurrencies = async () => {
    try {
      const { data: currencyData } = await axios.get(
        `${API_BASE_URL}/api/v1/getAllCurrencies`,
      );
      setCurrencies(currencyData);
    } catch (error) {
      toast.error("Failed to fetch currencies");
      console.error(error);
    }
  };

  useEffect(() => {
    getAllCurrencies();
  }, []);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            accessToken: tokenResponse.access_token,
            parentId: process.env.REACT_APP_SUPERADMIN_ID,
          }),
        });

        if (!response.ok) {
          throw new Error("Google login failed");
        }

        const data = await response.json();

        dispatch(loginSuccess(data));
        saveToLocalStorage(data);

        localStorage.setItem("name", data.name);
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem("tokenExpiresIn", data.expiresIn);
        localStorage.setItem("role", data.role);
        localStorage.setItem("id", data._id);
        localStorage.setItem("email", data.email);
        localStorage.setItem("isLoggedIn", true);
        localStorage.setItem("baseCurrency", data.baseCurrency);
        localStorage.setItem("createdAt", data.createdAt);
        localStorage.setItem("notifyOnExpense", data.notifyOnExpense);
        localStorage.setItem("notifyOnProject", data.notifyOnProject);

        dispatch(setRole(data.role));

        toast.success("Login successful via Google!");
        if (data.baseCurrency === null) {
          setShowCurrencyModal(true);
          return;
        } else {
          dispatch(setIsLoggedIn(true));
          setTimeout(() => {
            if (data.role === process.env.REACT_APP_SUPERADMIN_ROLE_ID) {
              navigate("/admin/dashboard");
            } else if (data.role === process.env.REACT_APP_USER_ROLE_ID) {
              navigate("/user/dashboard");
            } else {
              navigate("/dashboard");
            }
          }, 1000);
        }
      } catch (err) {
        toast.error(err.message || "Google login failed");
      }
    },
    onError: () => toast.error("Google login failed"),
  });

  const handleFacebookLogin = async (fbResponse) => {
    try {
      const { accessToken, userID } = fbResponse;
      const parentId = process.env.REACT_APP_SUPERADMIN_ID;
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/facebook`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken, userID, parentId }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Facebook login failed");
      }

      const data = await response.json();

      // SAME AS GOOGLE
      dispatch(loginSuccess(data));
      saveToLocalStorage(data);

      localStorage.setItem("name", data.name);
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);
      localStorage.setItem("role", data.role);
      localStorage.setItem("id", data._id);
      localStorage.setItem("email", data.email);
      localStorage.setItem("isLoggedIn", true);
      localStorage.setItem("createdAt", data.createdAt);
      localStorage.setItem("notifyOnExpense", data.notifyOnExpense);
      localStorage.setItem("notifyOnProject", data.notifyOnProject);

      toast.success("Login successful via Facebook!");

      if (data.baseCurrency === null) {
        setShowCurrencyModal(true);
        return;
      }

      dispatch(setIsLoggedIn(true));

      setTimeout(() => {
        if (data.role === process.env.REACT_APP_SUPERADMIN_ROLE_ID) {
          navigate("/admin/dashboard");
        } else if (data.role === process.env.REACT_APP_USER_ROLE_ID) {
          navigate("/user/dashboard");
        } else {
          navigate("/dashboard");
        }
      }, 1000);
    } catch (err) {
      console.error("Facebook login error:", err);
      toast.error(err.message || "Facebook login failed");
    }
  };

  useEffect(() => {
    const handleLinkedInMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      const { type, payload, error } = event.data || {};

      if (type === "LINKEDIN_SUCCESS") {
        const data = payload;

        dispatch(loginSuccess(data));
        saveToLocalStorage(data);

        localStorage.setItem("name", data.name);
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem("role", data.role);
        localStorage.setItem("id", data._id);
        localStorage.setItem("email", data.email);
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("baseCurrency", data.baseCurrency);
        localStorage.setItem("createdAt", data.createdAt);
        localStorage.setItem("notifyOnExpense", data.notifyOnExpense);
        localStorage.setItem("notifyOnProject", data.notifyOnProject);

        dispatch(setRole(data.role));

        toast.success("Login successful via LinkedIn!");

        if (data.baseCurrency === null) {
          setShowCurrencyModal(true);
          return;
        }

        dispatch(setIsLoggedIn(true));

        setTimeout(() => {
          if (data.role === process.env.REACT_APP_SUPERADMIN_ROLE_ID) {
            navigate("/admin/dashboard");
          } else if (data.role === process.env.REACT_APP_USER_ROLE_ID) {
            navigate("/user/dashboard");
          } else {
            navigate("/dashboard");
          }
        }, 500);
      }

      if (type === "LINKEDIN_ERROR") {
        toast.error(error || "LinkedIn login failed");
      }
    };

    window.addEventListener("message", handleLinkedInMessage);
    return () => window.removeEventListener("message", handleLinkedInMessage);
  }, [dispatch, navigate]);
  const saveBaseCurrency = async () => {
    if (!selectedCurrency) {
      toast.error("Please select a base currency");
      return;
    }

    try {
      setSavingCurrency(true);
      const userId = localStorage.getItem("id");
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/auth/update-base-currency`,
        {
          baseCurrency: selectedCurrency,
          userId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      const updatedUser = {
        ...JSON.parse(localStorage.getItem("user") || "{}"),
        baseCurrency: selectedCurrency,
      };

      localStorage.setItem("baseCurrency", selectedCurrency);
      saveToLocalStorage({
        ...updatedUser,
        baseCurrency: selectedCurrency,
      });

      dispatch(
        loginSuccess({
          ...updatedUser,
          baseCurrency: selectedCurrency,
        }),
      );

      toast.success("Base currency saved successfully");

      setShowCurrencyModal(false);
      dispatch(setIsLoggedIn(true));

      setTimeout(() => {
        if (
          localStorage.getItem(role) ===
          process.env.REACT_APP_SUPERADMIN_ROLE_ID
        ) {
          navigate("/admin/dashboard");
        } else if (
          localStorage.getItem(role) === process.env.REACT_APP_USER_ROLE_ID
        ) {
          navigate("/user/dashboard");
        } else {
          navigate("/dashboard");
        }
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save base currency");
    } finally {
      setSavingCurrency(false);
    }
  };
  return (
    <div className="h-full flex items-center justify-center">
      {!otpSent ? (
        <div className="max-w-md w-full bg-white/95 backdrop-blur-lg py-8 px-8 rounded-2xl shadow-2xl border border-gray-100 transform transition-all duration-300 hover:shadow-3xl">
          {/* Header */}
          <div className="text-center mb-8">
            {/* <div className="inline-flex items-center justify-center bg-themeGradient w-16 h-16 rounded-2xl mb-4 shadow-xl">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
            </div> */}
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Create Account
            </h2>
            <p className="text-gray-500">Start managing your finances today</p>
          </div>

          <form onSubmit={handleSignUp} className="space-y-5">
            {/* Name Input */}
            <div className="transform transition-all duration-200 hover:translate-x-1">
              <CustomInput
                type="text"
                label="Name"
                value={name}
                placeholder="Enter your full name"
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) {
                    setErrors((prev) => ({ ...prev, name: undefined }));
                  }
                }}
                message={errors.name}
                requir={true}
              />
            </div>

            {/* Email Input */}
            <div className="transform transition-all duration-200 hover:translate-x-1">
              <CustomInput
                type="email"
                label="Email"
                value={email}
                placeholder="you@example.com"
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors((prev) => ({ ...prev, email: undefined }));
                  }
                }}
                message={errors.email}
                requir={true}
              />
            </div>

            {/* Base Currency Select */}
            <div className="transform transition-all duration-200 hover:translate-x-1">
              <CustomSelect
                label="Base Currency"
                value={baseCurrency}
                onChange={(value) => {
                  setBaseCurrency(value);
                  if (errors.baseCurrency) {
                    setErrors((prev) => ({ ...prev, baseCurrency: undefined }));
                  }
                }}
                options={currencies.map((currency) => ({
                  label: `${currency.symbol} ${currency.name}`,
                  value: currency._id,
                }))}
                useAntd
                placeholder="Select your currency"
                message={errors.baseCurrency}
                required="true"
              />
            </div>

            {/* Password Input */}
            <div className="transform transition-all duration-200 hover:translate-x-1">
              <label className="block text-gray-700 font-medium mb-2">
                Password<span className="text-red-500 ml-1">*</span>
              </label>
              <Input.Password
                type={showPassword ? "text" : "password"}
                value={password}
                placeholder="Create a strong password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.signUpPassword) {
                    setErrors((prev) => ({
                      ...prev,
                      signUpPassword: undefined,
                    }));
                  }
                }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
              {errors.signUpPassword && (
                <ErrorText message={errors.signUpPassword} />
              )}
            </div>

            {/* Confirm Password Input */}
            <div className="transform transition-all duration-200 hover:translate-x-1">
              <label className="block text-gray-700 font-medium mb-2">
                Confirm Password<span className="text-red-500 ml-1">*</span>
              </label>
              <Input.Password
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                placeholder="Re-enter your password"
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) {
                    setErrors((prev) => ({
                      ...prev,
                      confirmPassword: undefined,
                    }));
                  }
                }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              {errors.confirmPassword && (
                <ErrorText message={errors.confirmPassword} />
              )}
            </div>

            {/* Sign Up Button */}
            <button
              type="submit"
              className={`w-full mt-6 py-3 px-4 bg-themeGradient text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                isLoading ? "cursor-not-allowed opacity-70" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Account...
                </span>
              ) : (
                "Sign Up"
              )}
            </button>
            {/* Divider */}
            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="mx-4 text-gray-500 text-sm font-medium">
                or continue with
              </span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>

            <div className="flex justify-center items-center gap-5">
              <button
                type="button"
                onClick={() => {
                  console.log("Google Clicked");
                  googleLogin();
                }}
                className="transform transition-all duration-200 hover:scale-[1.09] active:scale-[0.98]"
              >
                <img
                  src="/icons/googleLogo.svg"
                  className="h-10 w-10"
                  alt="google"
                />
              </button>

              <LinkedIn
                clientId={process.env.REACT_APP_LINKEDIN_CLIENT_ID}
                redirectUri={`${window.location.origin}/linkedin`}
                scope="openid profile email"
              >
                {({ linkedInLogin }) => (
                  <button
                    type="button"
                    onClick={linkedInLogin}
                    className="transform transition-all duration-200 hover:scale-[1.09] active:scale-[0.98]"
                  >
                    <img
                      src="/icons/linkedinLogo.svg"
                      className="h-11 w-11"
                      alt="linkedIn"
                    />
                  </button>
                )}
              </LinkedIn>

              <FacebookLogin
                appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                onSuccess={(response) => {
                  handleFacebookLogin(response);
                }}
                onFail={(error) => {
                  console.log("Facebook Login Failed:", error);
                  toast.error("Facebook login failed");
                }}
                render={({ onClick }) => (
                  <button
                    type="button"
                    onClick={onClick}
                    className="transform transition-all duration-200 hover:scale-[1.09] active:scale-[0.98]"
                  >
                    <img
                      src="/icons/facebookLogo.svg"
                      className="h-11 w-11"
                      alt="facebook"
                    />
                  </button>
                )}
              />
            </div>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <span className="text-gray-600">
              Already have an account?{" "}
              <button
                onClick={toggleForm}
                className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
              >
                Sign in
              </button>
            </span>
          </div>
          {/* Currency Modal */}
          <Modal
            open={showCurrencyModal}
            footer={null}
            closable={false}
            centered
            className="currency-modal"
          >
            <div className="p-4">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-themeGradient rounded-full mb-4">
                  <span className="text-3xl">💰</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  Select Your Base Currency
                </h3>
                <p className="text-gray-600">
                  Choose the primary currency for your expense tracking
                </p>
              </div>

              <CustomSelect
                label="Base Currency"
                value={selectedCurrency}
                onChange={(value) => setSelectedCurrency(value)}
                options={currencies.map((c) => ({
                  label: `${c.symbol} ${c.name}`,
                  value: c._id,
                }))}
                placeholder="Select currency"
                useAntd
                requir="true"
              />

              <button
                disabled={!selectedCurrency || savingCurrency}
                onClick={saveBaseCurrency}
                className={`w-full mt-6 py-3 bg-themeGradient text-white font-semibold rounded-lg shadow-lg transition-all duration-200 ${
                  !selectedCurrency || savingCurrency
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:shadow-xl transform hover:scale-[1.02]"
                }`}
              >
                {savingCurrency ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  "Continue"
                )}
              </button>
            </div>
          </Modal>
        </div>
      ) : (
        <VerifyOtpForm
          email={email}
          formData={verifyData}
          onResendOtp={handleResendOtp}
        />
      )}
    </div>
  );
};

export default SignUp;
